<template>
  <div class="media-upload-container">
    <h2>Upload Media with Captions</h2>
    <p class="instructions">Drag and drop the items below to reorder them.</p>
    <div class="upload-area" @dragover.prevent @drop.prevent="handleDrop" @click="triggerFileInput">
      <p>Drag and drop files here or click to upload</p>
      <input type="file" ref="fileInput" @change="handleFileInput" multiple hidden />
    </div>
    <ul class="media-list">
      <li
        v-for="(media, index) in mediaList"
        :key="media.id"
        draggable="true"
        @dragstart="dragStart(index)"
        @dragover.prevent
        @drop="drop(index)"
        class="draggable-item"
      >
        <div class="media-item">
          <div class="upload-progress-bar" v-if="media.uploadProgress > 0 && media.uploadProgress < 100">
  <div class="progress-fill" :style="{ width: media.uploadProgress + '%' }"></div>
</div>
<small v-if="media.uploadProgress > 0 && media.uploadProgress < 100">
  {{ media.uploadProgress }}%
</small>

          <div class="drag-handle">
            <span class="drag-icon">☰</span>
          </div>

          <div class="media-preview">
  <template v-if="media.file || media.filePath">
    <div class="thumbnail-container">
      <!-- Video thumbnail for local file uploads -->
      <img
        v-if="isVideo(media) && media.thumbnail"
        :src="media.thumbnail"
        alt="Video thumbnail"
        class="media-thumbnail"
      />

      <!-- Fallback to actual video element if no thumbnail or for server-hosted videos -->
      <video
        v-else-if="isVideo(media)"
        :src="media.preview || media.filePath"
        controls
        muted
        class="media-preview-video"
      ></video>

      <!-- Image files -->
      <img
        v-else
        :src="media.preview || media.filePath"
        alt="Uploaded media preview"
        class="media-thumbnail"
      />

      <!-- Green checkmark overlay -->
      <!-- <span v-if="uploadedIds.has(media.id)" class="check-overlay">✔</span> -->
      <span v-if="uploadedIds && uploadedIds.has(media.id)" class="check-overlay">✔</span>

    </div>
  </template>
</div>



          <div class="media-details">
            <input
              type="text"
              placeholder="Enter caption"
              v-model="media.caption"
              @input="updateCaption(media.id, media.caption)"
            />
            <button class="remove-btn" @click="removeMedia(index)">Remove</button>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>


<script>
import axios from 'axios';

export default {
  data() {
    return {
      mediaList: [],
      dragIndex: null,
      uploadedIds: new Set(), // Add this line
    };
  },
  mounted() {
    this.fetchExistingMedia();
  },
  methods: {
    async fetchExistingMedia() {
  try {
    const response = await axios.get('https://firefly-setup.athenarc.gr/media');
    this.mediaList = response.data.map((media) => ({
      id: media._id,
      filePath: `https://firefly-setup.athenarc.gr/${media.filePath}`,
      caption: media.caption || '',
      sequence: media.sequence,
      type: media.type || 'image',
    }));

    // Always re-initialize after setting mediaList
    this.uploadedIds = new Set(this.mediaList.map(media => media.id));


  } catch (error) {
    console.error('Error fetching existing media:', error);
  }
},
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileInput(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
    },
    handleDrop(event) {
      const files = Array.from(event.dataTransfer.files);
      this.addFiles(files);
    },
    addFiles(files) {
  files.forEach((file) => {
    const reader = new FileReader();
    const extension = file.name.split('.').pop().toLowerCase();
    const isVideo = ['mp4', 'webm', 'ogg'].includes(extension);

    reader.onload = async (e) => {
      const preview = e.target.result;

      const mediaItem = {
        id: Date.now() + Math.random(),
        file,
        preview, // For image or video source
        caption: '',
        type: isVideo ? 'video' : 'image',
        uploadProgress: 0,
        thumbnail: null, // For the thumbnail!
      };

      if (isVideo) {
        mediaItem.thumbnail = await this.generateVideoThumbnail(preview);
        console.log('Thumbnail for video:', mediaItem.thumbnail);

      }

      this.mediaList.push(mediaItem);
    };

    reader.readAsDataURL(file);
  });
},
generateVideoThumbnail(videoSrc) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.src = videoSrc;
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';

    video.addEventListener('loadedmetadata', () => {
      // Wait until metadata is loaded so we can safely seek
      video.currentTime = Math.max(1, video.duration / 2); // go to 1s or halfway if shorter
    });

    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const thumbnail = canvas.toDataURL('image/png');
      resolve(thumbnail);
    });

    video.addEventListener('error', () => {
      console.warn('Thumbnail generation failed for video:', videoSrc);
      resolve(null);
    });
  });
},


    isVideo(media) {
  // const path = media.file?.name || media.filePath || '';
  // const ext = path.split('.').pop().toLowerCase();
  // return ['mp4', 'webm', 'ogg'].includes(ext);

  return media.type === 'video';

},
    async removeMedia(index) {
      const mediaId = this.mediaList[index].id;
      try {
        await axios.delete(`https://firefly-setup.athenarc.gr/media/${mediaId}`);
        this.mediaList.splice(index, 1);
        this.updateSequence();
      } catch (error) {
        console.error('Error removing media:', error);
        alert('Failed to remove media.');
      }
    },
    dragStart(index) {
      this.dragIndex = index;
    },
    drop(index) {
      const draggedItem = this.mediaList[this.dragIndex];
      this.mediaList.splice(this.dragIndex, 1);
      this.mediaList.splice(index, 0, draggedItem);
      this.updateSequence();
    },
    async updateSequence() {
  const updatedMedia = this.mediaList
    .filter((media) => typeof media.id === 'string' && media.id.length === 24) // Only MongoDB ObjectIds
    .map((media, index) => ({
      id: media.id,
      sequence: index,
    }));

  if (updatedMedia.length === 0) return;

  try {
    await axios.put('https://firefly-setup.athenarc.gr/update-media-sequence', updatedMedia);
  } catch (error) {
    console.error('Error updating sequence:', error);
  }
},
    async updateCaption(id, caption) {
      if (!id) return; // Skip for unsaved new media
      try {
        await axios.put(`https://firefly-setup.athenarc.gr/media/${id}`, { caption });
      } catch (error) {
        console.error('Error updating caption:', error);
      }
    },
//     async saveNewMedia() {
//   const newMedia = this.mediaList.filter((media) => media.file && media.uploadProgress < 100);
//   if (newMedia.length === 0) return;

//   for (const media of newMedia) {
//     const formData = new FormData();
//     formData.append('mediaFiles[]', media.file);
//     formData.append('captions[]', media.caption);
//     formData.append('sequence[]', this.mediaList.indexOf(media));

//     try {
//       await axios.post(
//         'https://firefly-setup.athenarc.gr/upload-media',
//         formData,
//         {
//           headers: { 'Content-Type': 'multipart/form-data' },
//           onUploadProgress: (progressEvent) => {
//             const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
//             media.uploadProgress = percent;
//           },
//         }
//       );
//     } catch (error) {
//       console.error('Upload failed for', media.file.name, error);
//     }
//   }

//   // Refresh media list after all uploads
//   this.fetchExistingMedia();
// },
async saveNewMedia() {
  const newMedia = this.mediaList.filter((media) => media.file && media.uploadProgress < 100);
  if (newMedia.length === 0) return;

  for (const media of newMedia) {
    const formData = new FormData();
    formData.append('mediaFiles', media.file);
    formData.append('captions', media.caption);
    formData.append('sequence', this.mediaList.indexOf(media));

    try {
      await axios.post(
        'https://firefly-setup.athenarc.gr/upload-media',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            media.uploadProgress = percent;
          },
        }
      );
    } catch (error) {
      console.error('Upload failed:', error);
    }
  }

  this.fetchExistingMedia(); // Refresh list
}
,

  },
  watch: {
    mediaList: {
      deep: true,
      handler() {
        this.saveNewMedia();
      },
    },
  },
};
</script>

<style>
/* General Styles */
.media-upload-container {
  max-width: 700px;
  margin: 20px auto;
  padding: 30px;
  background: #f9f9f9;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  font-family: Arial, sans-serif;
}

.instructions {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
}

/* Upload Area */
.upload-area {
  border: 2px dashed #007bff;
  padding: 30px;
  text-align: center;
  color: #555;
  background: #e9f7fe;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.3s;
}

.upload-area:hover {
  background: #d0f0fc;
}

/* Media List */
.media-list {
  margin-top: 20px;
  padding: 0;
  list-style: none;
}

.media-item {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 10px;
  padding: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
}

/* Drag Handle */
.drag-handle {
  cursor: grab;
  margin-right: 15px;
  display: flex;
  align-items: center;
}

.drag-handle:hover {
  color: #007bff;
}

.drag-icon {
  font-size: 18px;
  user-select: none;
}

/* Media Preview */
.media-preview img {
  max-width: 80px;
  max-height: 80px;
  border-radius: 4px;
  margin-right: 15px;
  object-fit: cover;
}

/* Media Details */
.media-details {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.media-details input {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  font-size: 14px;
  margin-bottom: 5px;
}

.remove-btn {
  background: #ff6b6b;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  transition: background 0.3s;
}

.remove-btn:hover {
  background: #ff4c4c;
}

/* Drag-and-Drop Highlight */
.draggable-item.dragging {
  background-color: #e9f7fe;
  box-shadow: 0 0 10px rgba(0, 123, 255, 0.5);
}

.media-preview-video {
  max-width: 80px;
  max-height: 80px;
  border-radius: 4px;
  margin-right: 15px;
  object-fit: cover;
}

.upload-progress-bar {
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 5px;
}

.progress-fill {
  height: 100%;
  background-color: #4caf50;
  transition: width 0.3s ease;
}



.thumbnail-container {
  position: relative;
  display: inline-block;
}

.media-thumbnail,
.media-preview-video {
  max-width: 80px;
  max-height: 80px;
  border-radius: 4px;
  margin-right: 15px;
  object-fit: cover;
}

.check-overlay {
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: #4caf50;
  color: white;
  font-size: 14px;
  padding: 4px;
  border-radius: 50%;
  font-weight: bold;
}


</style>

